import React from 'react';

// CSS-in-JS animations
const shimmerAnimation = `
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
`;

const pulseAnimation = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const fadeInAnimation = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject animations into document head
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = shimmerAnimation + pulseAnimation + fadeInAnimation + spinAnimation;
    document.head.appendChild(style);
}

// Skeleton components
interface SkeletonBoxProps {
    width: string;
    height: string;
    borderRadius?: string;
    style?: React.CSSProperties;
}

const SkeletonBox = ({ width, height, borderRadius = 'var(--radius-md)', style = {} }: SkeletonBoxProps) => (
    <div
        style={{
            width,
            height,
            borderRadius,
            background: 'linear-gradient(90deg, var(--bg-secondary) 25%, var(--bg-primary) 50%, var(--bg-secondary) 75%)',
            backgroundSize: '200px 100%',
            animation: 'shimmer 1.5s infinite',
            ...style
        }}
    />
);

interface SkeletonTextProps {
    lines?: number;
    height?: string;
    width?: string;
    style?: React.CSSProperties;
}

const SkeletonText = ({ lines = 1, height = '16px', width = '100%', style = {} }: SkeletonTextProps) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', ...style }}>
        {Array.from({ length: lines }).map((_, index) => (
            <SkeletonBox
                key={index}
                width={index === lines - 1 ? width : '100%'}
                height={height}
                style={{ opacity: 1 - (index * 0.1) }}
            />
        ))}
    </div>
);

interface SkeletonCardProps {
    children: React.ReactNode;
    style?: React.CSSProperties;
}

const SkeletonCard = ({ children, style = {} }: SkeletonCardProps) => (
    <div
        style={{
            background: 'var(--bg-primary)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-light)',
            padding: 'var(--space-6)',
            boxShadow: 'var(--shadow-sm)',
            animation: 'fadeIn 0.6s ease-out',
            ...style
        }}
    >
        {children}
    </div>
);

// Dashboard-specific skeleton components
export const WelcomeSkeleton = () => (
    <SkeletonCard style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <SkeletonBox width="300px" height="32px" />
            <SkeletonBox width="400px" height="20px" />
        </div>
    </SkeletonCard>
);

export const StatsSkeleton = () => (
    <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 'var(--space-4)',
        marginBottom: 'var(--space-6)'
    }}>
        {Array.from({ length: 3 }).map((_, index) => (
            <SkeletonCard key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    <SkeletonBox width="120px" height="20px" />
                    <SkeletonBox width="80px" height="32px" />
                </div>
            </SkeletonCard>
        ))}
    </div>
);

export const OrdersSkeleton = () => (
    <SkeletonCard style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <SkeletonBox width="200px" height="24px" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {Array.from({ length: 3 }).map((_, index) => (
                    <div
                        key={index}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(6, 1fr)',
                            gap: 'var(--space-4)',
                            padding: 'var(--space-3)',
                            background: 'var(--bg-secondary)',
                            borderRadius: 'var(--radius-md)',
                            animation: 'fadeIn 0.6s ease-out',
                            animationDelay: `${index * 0.1}s`
                        }}
                    >
                        <SkeletonBox width="60px" height="16px" />
                        <SkeletonBox width="80px" height="16px" />
                        <SkeletonBox width="70px" height="16px" />
                        <SkeletonBox width="60px" height="16px" />
                        <SkeletonBox width="80px" height="16px" />
                        <SkeletonBox width="100px" height="32px" />
                    </div>
                ))}
            </div>
        </div>
    </SkeletonCard>
);

export const QuickActionsSkeleton = () => (
    <SkeletonCard style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <SkeletonBox width="150px" height="24px" />
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'var(--space-4)'
            }}>
                {Array.from({ length: 3 }).map((_, index) => (
                    <div
                        key={index}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            padding: 'var(--space-4)',
                            background: 'var(--bg-secondary)',
                            borderRadius: 'var(--radius-md)',
                            animation: 'fadeIn 0.6s ease-out',
                            animationDelay: `${index * 0.1}s`
                        }}
                    >
                        <SkeletonBox width="48px" height="48px" borderRadius="50%" />
                        <SkeletonBox width="120px" height="20px" />
                        <SkeletonBox width="140px" height="16px" />
                    </div>
                ))}
            </div>
        </div>
    </SkeletonCard>
);

export const RecommendationsSkeleton = () => (
    <SkeletonCard>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <SkeletonBox width="200px" height="24px" />
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: 'var(--space-4)'
            }}>
                {Array.from({ length: 3 }).map((_, index) => (
                    <div
                        key={index}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-3)',
                            padding: 'var(--space-4)',
                            background: 'var(--bg-secondary)',
                            borderRadius: 'var(--radius-md)',
                            animation: 'fadeIn 0.6s ease-out',
                            animationDelay: `${index * 0.1}s`
                        }}
                    >
                        <SkeletonBox width="100%" height="150px" />
                        <SkeletonBox width="80%" height="20px" />
                        <SkeletonBox width="60px" height="20px" />
                        <SkeletonBox width="100%" height="36px" />
                    </div>
                ))}
            </div>
        </div>
    </SkeletonCard>
);

// Loading spinner component
interface LoadingSpinnerProps {
    size?: string;
    color?: string;
}

export const LoadingSpinner = ({ size = '40px', color = 'var(--primary-600)' }: LoadingSpinnerProps) => (
    <div
        style={{
            width: size,
            height: size,
            border: `3px solid var(--bg-secondary)`,
            borderTop: `3px solid ${color}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        }}
    />
);

// Main loading component
export const DashboardLoadingSkeleton = () => (
    <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: 'var(--space-6)',
        animation: 'fadeIn 0.3s ease-out'
    }}>
        <WelcomeSkeleton />
        <StatsSkeleton />
        <OrdersSkeleton />
        <QuickActionsSkeleton />
        <RecommendationsSkeleton />
    </div>
);

export default DashboardLoadingSkeleton; 